import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
// Import Types
import type { BonusBlocksReportTableRow, WorkflowAwardBlockReportRow } from './types';
// Import Components
import DataTable from 'components/common/DataTable';
import BonusBlockExpandedContent from './BonusBlockExpandedContent';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Context
import { useCJWorkflowBlocks } from 'context/cjWorkflowBlocksContext';
// Import Services
import { CustomerJourneyHttpService } from 'services/http';
// Import Configs
import { useBonusBlocksReportColumns } from './config';
// Import SCSS
import styles from './cjBonusReceivedPlayersSection.module.scss';

interface Props {
    workflowId: string;
    isActive?: boolean;
}

const BonusBlocksReport: FC<Props> = ({ workflowId, isActive = true }) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const { getBlockInfo, ensureLoaded: ensureBlocksLoaded } = useCJWorkflowBlocks();

    const [isLoading, setIsLoading] = useState(true);
    const [rows, setRows] = useState<BonusBlocksReportTableRow[]>([]);

    // Opening this sub-tab triggers the shared, cached workflow-blocks fetch used to resolve
    // each row's raw BlockName (element id) to a friendly display name.
    useEffect(() => {
        if (isActive) {
            ensureBlocksLoaded();
        }
    }, [isActive, ensureBlocksLoaded]);

    const { getWorkflowAwardBlocksReportRequest } = useCancelableRequests(
        { getWorkflowAwardBlocksReportRequest: CustomerJourneyHttpService.getWorkflowAwardBlocksReport },
        { takeLatest: ['getWorkflowAwardBlocksReportRequest'] },
    );

    useEffect(() => {
        if (isNil(workflowId) || !isActive) {
            return;
        }

        setIsLoading(true);
        doGetRequest(getWorkflowAwardBlocksReportRequest.request, {
            queryString: { workflowId },
            successCallback: (Data) => {
                const list: WorkflowAwardBlockReportRow[] = Data?.Data ?? [];

                setRows(
                    list.map((item, index) => ({
                        hasHover: true,
                        dragDisable: true,
                        isRowExpandable: true,
                        data: { rowKey: `${item.AwardId}-${index}`, ...item },
                    })),
                );
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoading(false);
            }
        });
    }, [workflowId, isActive, doGetRequest, getWorkflowAwardBlocksReportRequest]);

    const { columns } = useBonusBlocksReportColumns(t, getBlockInfo);

    const renderRowNestedContent = useCallback(() => {
        const renderNestedContent = ({ data }: BonusBlocksReportTableRow) => (
            <BonusBlockExpandedContent bonusId={data?.AwardId} blockName={data?.BlockName} />
        );
        return renderNestedContent;
    }, []);

    return (
        <DataTable
            name=""
            rowKey="rowKey"
            columnKey="dataKey"
            classNames={styles.bonusBlocksTable}
            data={rows}
            dataCount={rows.length}
            columns={columns}
            isLoading={isLoading}
            isVisibleOnlyDataTable={true}
            withPagination={false}
            withPageSelector={false}
            withHeaderActions={false}
            isRowExpandable={true}
            renderRowNestedContent={renderRowNestedContent}
        />
    );
};

export default memo(BonusBlocksReport);
